import * as Yup from "yup";

/**
 * Hanging Protocol V2 — the group envelope and the protocol (MedDream
 * "category") document.
 *
 * Paths mirror the document exactly, because Formik indexes `errors` by the same
 * path it indexes `values` by: a message for
 * `panelsLayout.panels[0].layout.viewports[3].rotation` is what puts the error
 * under that viewport's Rotation box rather than in a bar at the foot of the
 * step.
 *
 * Deliberately looser on names than {@link createHPSchema} (v1). Real V2
 * protocols are called `1x1`, `2x2` and `4-split`, and there is a group named
 * `MG` — v1's "at least three characters, must start with a letter" would make
 * every one of those unsavable by the person opening them. Blank is the only
 * name this rejects.
 */

/** `hp_group_v2.name` / `hp_protocol_v2.name` are varchar(255); this is the UI's limit. */
const NAME_MAX = 50;
const DESCRIPTION_MAX = 100;

/**
 * Blank means unset for every number on a viewport — none of them are required
 * and an empty box must not read as zero. Yup casts `""` to `NaN` and leaves
 * `null` alone, so both are turned back into "no value" before any test runs.
 */
const blankToUnset = (value, original) =>
  original === "" || original === null || original === undefined ? undefined : value;

/** An optional whole number with a range the viewers accept. */
const optionalInteger = (t, field, { min, max }) =>
  Yup.number()
    .transform(blankToUnset)
    .typeError(t("hanging-protocol.error.invalid", { field }))
    .integer(t("hanging-protocol.error.whole-number", { field }))
    .min(min, t("hanging-protocol.error.range", { field, min, max }))
    .max(max, t("hanging-protocol.error.range", { field, min, max }));

const name = (t) => {
  const field = t("hanging-protocol.name");
  return (
    Yup.string()
      // Non-strict, so this trims before `required` looks at the value: a name of
      // spaces is blank, which is what the server would say too.
      .trim()
      .required(t("hanging-protocol.error.blank", { field }))
      .max(NAME_MAX, t("hanging-protocol.error.max", { field, max: String(NAME_MAX) }))
  );
};

const description = (t) => {
  const field = t("hanging-protocol.description");
  return Yup.string()
    .nullable()
    .max(DESCRIPTION_MAX, t("hanging-protocol.error.max", { field, max: String(DESCRIPTION_MAX) }));
};

/**
 * One viewport's presentation numbers.
 *
 * Ranges are DICOM's, matching what the v1 schema already enforces for the same
 * three fields. They matter more here than they read: the inspector coerces with
 * `Number(next) || 0`, so before this an out-of-range entry was accepted, stored
 * and hung as something the author never typed.
 *
 * `windowing` is either null or `{ windowCenter, windowWidth }` — see
 * buildWindowing in ViewportInspector — so the object itself is nullable and its
 * two members are only reached when one has been filled in.
 */
const viewport = (t) =>
  Yup.object({
    windowing: Yup.object({
      windowWidth: optionalInteger(t, t("hanging-protocol.window-width"), { min: 1, max: 65535 }),
      windowCenter: optionalInteger(t, t("hanging-protocol.window-center"), {
        min: -32768,
        max: 32767,
      }),
    }).nullable(),
    rotation: optionalInteger(t, t("hanging-protocol.rotation"), { min: -360, max: 360 }),
    /* The only one that is not a whole number — MedDream's zoom factor is a
       multiplier, so 1.5 is meaningful and 0 is not. */
    customScaleValue: Yup.number()
      .transform(blankToUnset)
      .typeError(t("hanging-protocol.error.invalid", { field: t("hanging-protocol.custom-scale") }))
      .moreThan(
        0,
        t("hanging-protocol.error.range", {
          field: t("hanging-protocol.custom-scale"),
          min: 0,
          max: 100,
        })
      )
      .max(
        100,
        t("hanging-protocol.error.range", {
          field: t("hanging-protocol.custom-scale"),
          min: 0,
          max: 100,
        })
      ),
    priorStudy: optionalInteger(t, t("hanging-protocol.prior-study-number"), { min: 0, max: 99 }),
    otherStudy: optionalInteger(t, t("hanging-protocol.other-study-number"), { min: 0, max: 99 }),
  });

/**
 * The layout, carried only so the viewport paths resolve. Nothing about the grid
 * itself is validated here — its size is picked from a 4x4 board that cannot
 * produce an invalid value.
 */
const panelsLayout = (t) =>
  Yup.object({
    panels: Yup.array().of(
      Yup.object({
        layout: Yup.object({
          viewports: Yup.array().of(viewport(t)),
        }),
      })
    ),
  }).nullable();

/** The group envelope: everything else on the step is a toggle or a builder. */
export const createHpV2GroupSchema = (t) =>
  Yup.object({
    name: name(t),
    description: description(t),
  });

/** One protocol. */
export const createHpV2ProtocolSchema = (t) =>
  Yup.object({
    name: name(t),
    description: description(t),
    panelsLayout: panelsLayout(t),
  });
